import * as Yup from 'yup';
import { useAppDispatch, useAppSelector } from '../../../hooks/redux-hooks';
import { setToastInfo } from '../../../store/common.slice';
import { ToastType } from '../../../types/common.types';
import { ClipboardList } from 'lucide-react';
import { ErrorMessage, Form, Formik } from 'formik';
import { inputFieldStyles } from '../../../global-styles';
import Button from '../../common/Button';
import QuizService from '../../../services/quiz.service';
import { CreateQuizInput } from './types';
import SkillsOutcomesSelector from '../grading/SkillsOutcomesSelector';
import { useNavigate } from 'react-router-dom';
import { LabelWithTooltip } from '../../common/Tooltip';
import Switch from '../../common/Switch';
import ManageUsersandGroups from '../../common/ManageUsers&Groups';

export const QUESTION_TYPES = [
  'multiple choice',
  'true false',
  'short answer',
  'essay',
  'fill in the blank',
  'objective',
  'subjective',
];

const CreateQuizSchema = Yup.object().shape({
  name: Yup.string().required('Name is required.').min(1).max(300),
  skills: Yup.array().of(Yup.string()).min(1, 'At least one skill is required'),
  learningOutcomes: Yup.array()
    .of(Yup.string())
    .min(1, 'At least one learning outcome is required'),
  description: Yup.string().max(400),
  image: Yup.string(),
  allowedUserIds: Yup.array()
    .of(Yup.string())
    .min(1, 'At least one user is required'),
  allowedGroupIds: Yup.array().of(Yup.string()).default([]),
  numAttempts: Yup.number().min(1, 'At least 1 attempt required').default(1),
  numQuestions: Yup.number().min(0).default(0),
  timeLimit: Yup.number().min(1, 'At least 1 minute required'),
  deadline: Yup.string(),
  questionType: Yup.string()
    .oneOf(QUESTION_TYPES, 'Invalid question type')
    .required('Question type is required'),
  extractImages: Yup.boolean().default(false),
});

const CreateQuiz = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { orgInfo, userInfo } = useAppSelector((state) => state.common);

  const initialValues: CreateQuizInput = {
    name: '',
    skills: [],
    learningOutcomes: [],
    description: '',
    image: '',
    allowedUserIds: [],
    allowedGroupIds: [],
    numAttempts: 1,
    numQuestions: 10,
    timeLimit: 0,
    deadline: '',
    questionType: QUESTION_TYPES[0],
    extractImages: false,
    userId: userInfo?._id || '',
  };

  const saveQuiz = async (values: CreateQuizInput) => {
    try {
      const payload = {
        ...values,
        deadline: values.deadline
          ? new Date(values.deadline).toISOString()
          : '',
      };
      const response = await QuizService.createQuiz(payload);
      if (response.data.data && response.data.data.quiz) {
        dispatch(
          setToastInfo({
            isOpen: true,
            message: response.data.message,
            type: ToastType.SUCCESS,
          })
        );
        navigate(`/quiz/${response.data.data.quiz._id}`);
      } else {
        dispatch(
          setToastInfo({
            isOpen: true,
            message: response.data.message,
            type: ToastType.ERROR,
          })
        );
      }
    } catch (err: any) {
      console.log('err: ', err);
      dispatch(
        setToastInfo({
          isOpen: true,
          message: err?.response?.data?.message || 'Error in creating quiz.',
          type: ToastType.ERROR,
        })
      );
    }
  };

  return (
    <div className="overflow-y-auto w-full">
      <div className="flex items-center justify-between pr-7">
        <div className="flex items-center w-full px-4 py-2 gap-3 border-b border-[#E2E8F0]">
          <i
            className="icon-arrow-up rotate-[-90deg] text-[24px] cursor-pointer"
            onClick={() => navigate('/quiz')}
          />
          <span className="text-[20px] font-medium">Create Quiz</span>
        </div>
      </div>

      <Formik
        initialValues={initialValues}
        validationSchema={CreateQuizSchema}
        validateOnChange={true}
        enableReinitialize={true}
        onSubmit={async (values, { resetForm }) => {
          await saveQuiz(values);
        }}
      >
        {({
          touched,
          handleChange,
          handleBlur,
          isValid,
          values,
          setFieldValue,
          dirty,
          errors,
        }) => {
          return (
            <>
              <Form className="h-[calc(100%-50px)]">
                <div className="w-full h-full flex justify-between">
                  <div className="p-5 flex overflow-y-auto w-[60%]">
                    <div className="flex flex-col flex-grow">
                      <div className="mb-5">
                        <label className="text-[14px] text-[#020817] font-medium mb-2 inline-block">
                          <LabelWithTooltip
                            label="Quiz Name *"
                            tooltip="Give your quiz a descriptive name."
                            direction="right"
                          />
                        </label>
                        <input
                          id="name"
                          type="text"
                          placeholder="e.g., Python Basics Quiz"
                          onChange={handleChange}
                          onBlur={handleBlur}
                          name="name"
                          value={values.name}
                          className={inputFieldStyles}
                        />
                        <ErrorMessage
                          name="name"
                          render={(msg) => (
                            <div className="text-red-500 text-[12px] mt-1">
                              {msg}
                            </div>
                          )}
                        />
                      </div>

                      <div className="mb-5">
                        <label className="text-[14px] text-[#020817] font-medium mb-2 inline-block">
                          <LabelWithTooltip
                            label="Description"
                            tooltip="Provide a brief description of the quiz."
                            direction="right"
                          />
                        </label>
                        <input
                          id="description"
                          type="text"
                          placeholder="e.g., A quiz covering Python fundamentals"
                          onChange={handleChange}
                          onBlur={handleBlur}
                          name="description"
                          value={values.description}
                          className={inputFieldStyles}
                        />
                        <ErrorMessage
                          name="description"
                          render={(msg) => (
                            <div className="text-red-500 text-[12px] mt-1">
                              {msg}
                            </div>
                          )}
                        />
                      </div>

                      <div className="mb-5">
                        <label className="text-[14px] text-[#020817] font-medium mb-2 inline-block">
                          <LabelWithTooltip
                            label="Skills & Learning Outcomes *"
                            tooltip="Tag your quiz with skills being assessed and learning outcomes students should achieve."
                            direction="right"
                          />
                        </label>
                        <SkillsOutcomesSelector
                          selectedSkills={values.skills}
                          selectedOutcomes={values.learningOutcomes}
                          onSkillsChange={(skills) =>
                            setFieldValue('skills', skills)
                          }
                          onOutcomesChange={(outcomes) =>
                            setFieldValue('learningOutcomes', outcomes)
                          }
                        />
                        {(touched.skills && errors.skills) ||
                        (touched.learningOutcomes &&
                          errors.learningOutcomes) ? (
                          <div className="text-red-500 text-[12px] mt-1">
                            {errors.skills || errors.learningOutcomes}
                          </div>
                        ) : null}
                      </div>

                      <div className="mb-5">
                        <label className="text-[14px] text-[#020817] font-medium mb-2 inline-block">
                          <LabelWithTooltip
                            label="Question Type *"
                            tooltip="Select the type of questions for this quiz."
                            direction="right"
                          />
                        </label>
                        <select
                          id="questionType"
                          name="questionType"
                          onChange={handleChange}
                          onBlur={handleBlur}
                          value={values.questionType}
                          className={inputFieldStyles}
                        >
                          {QUESTION_TYPES.map((type) => (
                            <option key={type} value={type}>
                              {type.charAt(0).toUpperCase() + type.slice(1)}
                            </option>
                          ))}
                        </select>
                        <ErrorMessage
                          name="questionType"
                          render={(msg) => (
                            <div className="text-red-500 text-[12px] mt-1">
                              {msg}
                            </div>
                          )}
                        />
                      </div>

                      <div className="grid grid-cols-2 gap-4 mb-5">
                        <div>
                          <label className="text-[14px] text-[#020817] font-medium mb-2 inline-block">
                            <LabelWithTooltip
                              label="Number of Questions"
                              tooltip="How many questions should this quiz contain."
                              direction="right"
                            />
                          </label>
                          <input
                            id="numQuestions"
                            type="number"
                            min={0}
                            placeholder="e.g., 10"
                            onChange={handleChange}
                            onBlur={handleBlur}
                            name="numQuestions"
                            value={values.numQuestions}
                            className={inputFieldStyles}
                          />
                        </div>
                        <div>
                          <label className="text-[14px] text-[#020817] font-medium mb-2 inline-block">
                            <LabelWithTooltip
                              label="Number of Attempts"
                              tooltip="How many times a student can attempt this quiz."
                              direction="right"
                            />
                          </label>
                          <input
                            id="numAttempts"
                            type="number"
                            min={1}
                            placeholder="e.g., 1"
                            onChange={handleChange}
                            onBlur={handleBlur}
                            name="numAttempts"
                            value={values.numAttempts}
                            className={inputFieldStyles}
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-4 mb-5">
                        <div>
                          <label className="text-[14px] text-[#020817] font-medium mb-2 inline-block">
                            <LabelWithTooltip
                              label="Time Limit (minutes)"
                              tooltip="Time limit for the quiz in minutes. Set 0 for no limit."
                              direction="right"
                            />
                          </label>
                          <input
                            id="timeLimit"
                            type="number"
                            min={0}
                            placeholder="e.g., 30"
                            onChange={handleChange}
                            onBlur={handleBlur}
                            name="timeLimit"
                            value={values.timeLimit}
                            className={inputFieldStyles}
                          />
                        </div>
                        <div>
                          <label className="text-[14px] text-[#020817] font-medium mb-2 inline-block">
                            <LabelWithTooltip
                              label="Deadline"
                              tooltip="Set a deadline for quiz completion."
                              direction="right"
                            />
                          </label>
                          <input
                            id="deadline"
                            type="datetime-local"
                            onChange={handleChange}
                            onBlur={handleBlur}
                            name="deadline"
                            value={values.deadline}
                            className={inputFieldStyles}
                          />
                        </div>
                      </div>

                      <div className="pb-5">
                        <ManageUsersandGroups
                          onUserSelect={(selectedUserIds) => {
                            setFieldValue('allowedUserIds', selectedUserIds);
                          }}
                          onGroupSelect={(selectedGroupIds) => {
                            setFieldValue('allowedGroupIds', selectedGroupIds);
                          }}
                          selectedUserIds={values.allowedUserIds || []}
                          selectedGroupIds={values.allowedGroupIds || []}
                        />
                      </div>
                      {orgInfo?.extractImages && (
                        <div className="pb-8 flex items-center justify-between space-x-3">
                          <div className="">
                            <Switch
                              label="Allow Image Extraction"
                              handleClick={() => {
                                setFieldValue(
                                  'extractImages',
                                  !values.extractImages
                                );
                              }}
                              disabled={orgInfo.extractImages ? false : true}
                              active={values.extractImages as boolean}
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="w-[40%] flex flex-col p-5 flex-grow items-center border-l border-[#E2E8F0] overflow-y-auto">
                    <div className="w-[48px] h-[48px] rounded-full bg-[#e3e2e7] flex shrink-0 justify-center items-center">
                      <ClipboardList color="black" />
                    </div>
                    <p className="text-[20px] font-semibold text-[#020817] mt-4">
                      Start Building
                    </p>

                    <div className="max-w-[400px]">
                      <div className="flex gap-3 mt-4">
                        <div className="w-[22px] h-[22px] rounded-full bg-[#e3e2e7] flex justify-center items-center text-[14px] shrink-0 mt-2">
                          1
                        </div>
                        <div>
                          <h4 className="text-[#020817] text-[14px] font-medium">
                            Enter Quiz Name
                          </h4>
                          <p className="text-[#64748B] text-[14px]">
                            Provide a clear, unique name for your quiz. Example:
                            Python Basics Quiz.
                          </p>
                        </div>
                      </div>
                      <div className="flex gap-3 mt-5">
                        <div className="w-[22px] h-[22px] rounded-full bg-[#e3e2e7] flex justify-center items-center text-[14px] shrink-0 mt-2">
                          2
                        </div>
                        <div>
                          <h4 className="text-[#020817] text-[14px] font-medium">
                            Select Skills
                          </h4>
                          <p className="text-[#64748B] text-[14px]">
                            Choose the skills that this quiz will assess. This
                            ensures focused and relevant testing.
                          </p>
                        </div>
                      </div>
                      <div className="flex gap-3 mt-5">
                        <div className="w-[22px] h-[22px] rounded-full bg-[#e3e2e7] flex justify-center items-center text-[14px] shrink-0 mt-2">
                          3
                        </div>
                        <div>
                          <h4 className="text-[#020817] text-[14px] font-medium">
                            Define Learning Outcomes
                          </h4>
                          <p className="text-[#64748B] text-[14px]">
                            Specify what students should be able to accomplish
                            after completing this quiz.
                          </p>
                        </div>
                      </div>
                      <div className="flex gap-3 mt-5">
                        <div className="w-[22px] h-[22px] rounded-full bg-[#e3e2e7] flex justify-center items-center text-[14px] shrink-0 mt-2">
                          4
                        </div>
                        <div>
                          <h4 className="text-[#020817] text-[14px] font-medium">
                            Configure Quiz Settings
                          </h4>
                          <p className="text-[#64748B] text-[14px]">
                            Set the number of questions, attempts allowed, time
                            limit, and deadline for the quiz.
                          </p>
                        </div>
                      </div>
                      <div className="flex gap-3 mt-5">
                        <div className="w-[22px] h-[22px] rounded-full bg-[#e3e2e7] flex justify-center items-center text-[14px] shrink-0 mt-2">
                          5
                        </div>
                        <div>
                          <h4 className="text-[#020817] text-[14px] font-medium">
                            Assign Users
                          </h4>
                          <p className="text-[#64748B] text-[14px]">
                            Select which students or groups should have access
                            to this quiz.
                          </p>
                        </div>
                      </div>

                      <div className="text-center mt-7">
                        <Button
                          disabled={!dirty || !isValid || !touched}
                          type={'submit'}
                        >
                          Create Quiz
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              </Form>
            </>
          );
        }}
      </Formik>
    </div>
  );
};

export default CreateQuiz;
